import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { getApartmentRegistry, saveApartmentRegistry } from '../services/firestoreService';
import {
  generateApartmentRegistryPDF,
  APARTMENT_REGISTRY_HEADERS,
  apartmentRowToCells
} from '../services/pdfService';
import PageHero from '../components/common/PageHero';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import Loading from '../components/common/Loading';
import { downloadCsvFile } from '../utils/helpers';
import toast from 'react-hot-toast';

const newRowId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export const emptyApartmentRow = () => ({
  id: newRowId(),
  nameApartments: '',
  xafiiskaApartments: '',
  phoneNumber: '',
  wardiyeName: '',
  wardiyePhone: '',
  degmada: '',
  location: '',
  date: ''
});

const INITIAL_BLANK_ROWS = 15;

const normalizeLoadedRow = (row) => ({
  id: row.id || newRowId(),
  nameApartments: row.nameApartments || '',
  xafiiskaApartments: row.xafiiskaApartments || '',
  phoneNumber: row.phoneNumber || '',
  wardiyeName: row.wardiyeName || '',
  wardiyePhone: row.wardiyePhone || '',
  degmada: row.degmada || '',
  location: row.location || '',
  date: row.date || ''
});

const cellInput =
  'w-full min-w-[7rem] border-0 bg-transparent px-2 py-1.5 text-sm text-gray-900 outline-none focus:bg-yellow-50';

const ApartmentRegistry = () => {
  const { user } = useAuth();
  const auditActor = user ? { uid: user.uid, email: user.email } : null;

  const [rows, setRows] = useState(() => Array.from({ length: INITIAL_BLANK_ROWS }, emptyApartmentRow));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const result = await getApartmentRegistry();
    setLoading(false);
    if (!result.success) {
      toast.error(result.error || 'Load failed');
      return;
    }
    const loaded = result.data?.rows || [];
    if (loaded.length) {
      setRows(loaded.map(normalizeLoadedRow));
    } else {
      setRows(Array.from({ length: INITIAL_BLANK_ROWS }, emptyApartmentRow));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updateCell = (rowId, field, value) => {
    setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, [field]: value } : r)));
  };

  const addRow = () => {
    setRows((prev) => [...prev, emptyApartmentRow()]);
  };

  const removeRow = (rowId) => {
    setRows((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((r) => r.id !== rowId);
    });
  };

  const handleSave = async () => {
    setSaving(true);
    const result = await saveApartmentRegistry(rows, auditActor);
    setSaving(false);
    if (result.success) toast.success('Waa la keydiyay');
    else toast.error(result.error || 'Keydinta waa fashilantay');
  };

  const exportCsv = () => {
    const dataRows = rows.map((row, i) => apartmentRowToCells(row, i));
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsvFile(`apartment-registry-${stamp}.csv`, APARTMENT_REGISTRY_HEADERS, dataRows);
    toast.success(`CSV: ${rows.length} saf`);
  };

  const exportPdf = async () => {
    try {
      await generateApartmentRegistryPDF(rows);
      toast.success('PDF waa la soo dejiyay');
    } catch {
      toast.error('PDF khalad');
    }
  };

  if (loading) return <Loading fullScreen text="Soo raraya diiwaanka..." />;

  return (
    <div className="space-y-6">
      <PageHero
        title="Diiwaanka Guryaha"
        subtitle="Ku dar saf, buuxi xogta, keydi — soo deji PDF ama CSV"
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={exportCsv} icon={<span>📥</span>}>
              CSV
            </Button>
            <Button variant="secondary" onClick={exportPdf} icon={<span>📄</span>}>
              PDF
            </Button>
            <Button variant="outline" onClick={addRow}>
              + Saf cusub
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Keydinaya…' : 'Keydi'}
            </Button>
          </div>
        }
      />

      <Card className="!p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1100px] border-collapse text-left">
            <thead>
              <tr className="bg-yellow-300 text-black">
                <th className="border border-black px-2 py-2 text-xs font-bold uppercase w-12">NO</th>
                <th className="border border-black px-2 py-2 text-xs font-bold uppercase">Name Apparments</th>
                <th className="border border-black px-2 py-2 text-xs font-bold uppercase">Xafiiska Apparments</th>
                <th className="border border-black px-2 py-2 text-xs font-bold uppercase">Phone Number</th>
                <th className="border border-black px-2 py-2 text-xs font-bold uppercase">Magaca Wardiye</th>
                <th className="border border-black px-2 py-2 text-xs font-bold uppercase">Phone Number</th>
                <th className="border border-black px-2 py-2 text-xs font-bold uppercase">Degmada</th>
                <th className="border border-black px-2 py-2 text-xs font-bold uppercase">Location</th>
                <th className="border border-black px-2 py-2 text-xs font-bold uppercase">Date</th>
                <th className="border border-black px-2 py-2 text-xs font-bold uppercase w-14" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={row.id} className="bg-white hover:bg-gray-50">
                  <td className="border border-gray-300 px-2 py-1 text-center text-sm font-medium text-gray-700">
                    {index + 1}
                  </td>
                  <td className="border border-gray-300 p-0">
                    <input
                      className={cellInput}
                      value={row.nameApartments}
                      onChange={(e) => updateCell(row.id, 'nameApartments', e.target.value)}
                      placeholder="—"
                    />
                  </td>
                  <td className="border border-gray-300 p-0">
                    <input
                      className={cellInput}
                      value={row.xafiiskaApartments}
                      onChange={(e) => updateCell(row.id, 'xafiiskaApartments', e.target.value)}
                    />
                  </td>
                  <td className="border border-gray-300 p-0">
                    <input
                      className={cellInput}
                      value={row.phoneNumber}
                      onChange={(e) => updateCell(row.id, 'phoneNumber', e.target.value)}
                    />
                  </td>
                  <td className="border border-gray-300 p-0">
                    <input
                      className={cellInput}
                      value={row.wardiyeName}
                      onChange={(e) => updateCell(row.id, 'wardiyeName', e.target.value)}
                    />
                  </td>
                  <td className="border border-gray-300 p-0">
                    <input
                      className={cellInput}
                      value={row.wardiyePhone}
                      onChange={(e) => updateCell(row.id, 'wardiyePhone', e.target.value)}
                    />
                  </td>
                  <td className="border border-gray-300 p-0">
                    <input
                      className={cellInput}
                      value={row.degmada}
                      onChange={(e) => updateCell(row.id, 'degmada', e.target.value)}
                    />
                  </td>
                  <td className="border border-gray-300 p-0">
                    <input
                      className={cellInput}
                      value={row.location}
                      onChange={(e) => updateCell(row.id, 'location', e.target.value)}
                    />
                  </td>
                  <td className="border border-gray-300 p-0">
                    <input
                      type="date"
                      className={cellInput}
                      value={row.date}
                      onChange={(e) => updateCell(row.id, 'date', e.target.value)}
                    />
                  </td>
                  <td className="border border-gray-300 px-1 py-1 text-center">
                    <button
                      type="button"
                      className="text-xs text-red-600 hover:text-red-800 disabled:opacity-40"
                      onClick={() => removeRow(row.id)}
                      disabled={rows.length <= 1}
                      title="Tirtir saf"
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <p className="text-sm text-gray-500">
        {rows.length} saf · Guji <strong>Keydi</strong> si xogtu ugu keyd gasho Firebase.
      </p>
    </div>
  );
};

export default ApartmentRegistry;
